import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import "../../styles/Dashboard.css";
import { getData, postData, putData } from "../../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../../context/LoaderContext";

const emptyInvoice = {
  invoiceType: "Quotation",
  invoiceDate: new Date().toISOString().slice(0, 10),
  dueDate: new Date().toISOString().slice(0, 10),
  sellerNTNCNIC: "",
  sellerBusinessName: "",
  sellerBranchName: "",
  sellerProvince: "",
  sellerAddress: "",
  salesPersonName: "",
  buyerNTNCNIC: "",
  buyerBusinessName: "",
  buyerProvince: "",
  buyerAddress: "",
  buyerRegistrationType: "",
  invoiceRefNo: "",
  items: [],
};

const emptyItem = {
  productId: "",
  hsCode: "",
  productName: "",
  productDescription: "",
  rate: "",
  baseTaxPercent: "",
  uoM: "",
  quantity: "1",
  totalValues: "0",
  valueSalesExcludingST: "",
  fixedNotifiedValueOrRetailPrice: "0",
  salesTaxApplicable: "",
  salesTaxWithheldAtSource: "0",
  extraTax: "0",
  furtherTax: "0",
  sroScheduleNo: "",
  fedPayable: "0",
  discount: "0",
  saleType: "Goods at standard rate (default)",
  sroItemSerialNo: "",
};

const numericItemFields = [
  "quantity",
  "totalValues",
  "valueSalesExcludingST",
  "fixedNotifiedValueOrRetailPrice",
  // salesTaxApplicable handled separately as an amount
  "salesTaxWithheldAtSource",
  "extraTax",
  "furtherTax",
  "fedPayable",
  "discount",
];

const getSaleId = (sale) => sale?.id ?? sale?._id ?? "";

const getBuyerId = (buyer) => buyer?.id ?? buyer?._id ?? "";

const getProductId = (product) => product?.id ?? product?._id ?? "";

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.sales)) return payload.sales;
  return [];
};

const parseItemsField = (items) => {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === "string") {
    try {
      const parsed = JSON.parse(items);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Failed to parse items", error);
      return [];
    }
  }
  return [];
};

const parseStoredJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to parse stored value", error);
    return null;
  }
};

const sanitizeDecimal = (value) => {
  const clean = String(value ?? "").replace(/[^0-9.]/g, "");
  if (!clean.includes(".")) return clean;
  const [whole, ...rest] = clean.split(".");
  return `${whole}.${rest.join("").slice(0, 4)}`;
};

const round2 = (value) =>
  Number.isFinite(Number(value))
    ? Math.round((Number(value) + Number.EPSILON) * 100) / 100
    : 0;

const calcLineTax = (item) => {
  const qty = parseFloat(item.quantity) || 0;
  const price = parseFloat(item.valueSalesExcludingST) || 0;
  const discount = parseFloat(item.discount) || 0;
  const base = Math.max(qty * price - discount, 0);
  const salesTaxAmount = parseFloat(item.salesTaxApplicable) || 0;
  const percent =
    (parseFloat(item.extraTax) || 0) +
    (parseFloat(item.furtherTax) || 0) +
    (parseFloat(item.salesTaxWithheldAtSource) || 0);
  return round2(salesTaxAmount + base * (percent / 100));
};

export default function Quotation() {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { saleId: saleIdParam } = useParams();
  const location = useLocation();
  const saleState = location.state?.sale;
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoiceData, setInvoiceData] = useState(emptyInvoice);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sellerProfile, setSellerProfile] = useState({
    branchName: "",
    businessName: "",
    province: "",
    address: "",
    cnic: "",
  });
  const [salesPerson, setSalesPerson] = useState("");
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const hasFetchedInitial = useRef(false);

  const selectedBuyerId = useMemo(() => {
    const match = buyers.find(
      (buyer) =>
        buyer?.cnic === invoiceData.buyerNTNCNIC ||
        buyer?.business_name === invoiceData.buyerBusinessName ||
        buyer?.buyerBusinessName === invoiceData.buyerBusinessName,
    );
    return match ? getBuyerId(match) : "";
  }, [buyers, invoiceData.buyerBusinessName, invoiceData.buyerNTNCNIC]);

  const buyerOptions = useMemo(
    () =>
      buyers.map((buyer) => {
        const id = getBuyerId(buyer);
        const province = buyer?.province || buyer?.provance || "";
        const name =
          buyer?.buyerBusinessName ||
          buyer?.business_name ||
          buyer?.buyer_name ||
          "Buyer";
        return {
          value: id,
          label: `${name}${province ? ` (${province})` : ""}`,
        };
      }),
    [buyers],
  );

  const selectedBuyerOption = useMemo(
    () =>
      buyerOptions.find(
        (opt) => String(opt.value) === String(selectedBuyerId),
      ) || null,
    [buyerOptions, selectedBuyerId],
  );

  const productOptions = useMemo(
    () =>
      products.map((product) => {
        const id = getProductId(product);
        const productLabel =
          product?.name ||
          product?.productName ||
          product?.description ||
          product?.hscode ||
          "Product";
        return { value: id, label: productLabel };
      }),
    [products],
  );

  const fetchDropdownData = async () => {
    try {
      showLoader();
      const [buyersRes, productsRes] = await Promise.all([
        getData("api/buyer"),
        getData("api/products"),
      ]);
      setBuyers(normaliseList(buyersRes));
      setProducts(normaliseList(productsRes));
    } catch (error) {
      toast.error("Dropdown data not loaded.");
      console.error("Failed to fetch dropdown data", error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const storedBranch = parseStoredJson(localStorage.getItem("branch"));
    const storedUser = parseStoredJson(localStorage.getItem("user"));
    const branchName =
      storedBranch?.branch_name ||
      storedBranch?.seller_name ||
      storedBranch?.business_name ||
      "";
    const businessName =
      storedBranch?.business_name ||
      storedBranch?.businessName ||
      storedBranch?.seller_name ||
      "";
    const branchProvince = storedBranch?.province || "";
    const branchAddress = storedBranch?.address || "";
    const branchCnic =
      storedBranch?.cnic ||
      storedBranch?.sellerNTNCNIC ||
      storedBranch?._id ||
      "";

    if (storedBranch) {
      setSellerProfile({
        branchName,
        businessName,
        province: branchProvince,
        address: branchAddress,
        cnic: branchCnic,
      });
      setInvoiceData((prev) => ({
        ...prev,
        sellerBranchName: branchName,
        sellerBusinessName: businessName,
        sellerNTNCNIC: branchCnic,
        sellerProvince: branchProvince,
        sellerAddress: branchAddress,
      }));
    }

    if (storedUser) {
      const name =
        storedUser?.name || storedUser?.username || storedUser?.fullName || "";
      setSalesPerson(name);
      setInvoiceData((prev) => ({
        ...prev,
        salesPersonName: name,
      }));
    }
  }, []);

  useEffect(() => {
    if (saleState) {
      const { card: _card, Card: _Card, ...restSaleState } = saleState || {};
      const saleItems = parseItemsField(saleState?.items);
      const mappedItems = saleItems.length
        ? saleItems.map((item) => {
            const qty = Number(item.quantity || 0);
            const price = Number(item.valueSalesExcludingST || 0);
            const discount = Number(item.discount || 0);
            const base = Math.max(qty * price - discount, 0);
            const salesTaxAmount = Number(item.salesTaxApplicable || 0);
            const inferredPercent =
              base > 0 ? (salesTaxAmount / base) * 100 : 0;
            return {
              ...item,
              baseTaxPercent: Number.isFinite(
                Number.parseFloat(String(item.rate || "").replace("%", "")),
              )
                ? Number.parseFloat(String(item.rate || "").replace("%", ""))
                : inferredPercent,
            };
          })
        : invoiceData.items;
      setInvoiceData((prev) => ({
        ...prev,
        ...restSaleState,
        invoiceDate: saleState?.invoiceDate
          ? String(saleState.invoiceDate).slice(0, 10)
          : prev.invoiceDate,
        dueDate: saleState?.dueDate
          ? String(saleState.dueDate).slice(0, 10)
          : prev.dueDate,
        items: mappedItems,
      }));
      setEditingSaleId(getSaleId(saleState));
      setItemForm(emptyItem);
      setEditingItemIndex(null);
    }
  }, [saleState]);

  useEffect(() => {
    if (saleIdParam) {
      loadSaleForEdit(saleIdParam);
    } else {
      setEditingSaleId(null);
    }
  }, [saleIdParam]);

  const handleItemChange = (event) => {
    const { name, value } = event.target;
    const nextValue = numericItemFields.includes(name)
      ? sanitizeDecimal(value)
      : value;
    setItemForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleBuyerSelect = (_, option) => {
    const buyerId = option?.value || option?.id || option || "";
    const buyer = buyers.find(
      (item) => String(getBuyerId(item)) === String(buyerId),
    );
    if (!buyer) {
      setInvoiceData((prev) => ({
        ...prev,
        buyerNTNCNIC: "",
        buyerBusinessName: "",
        buyerProvince: "",
        buyerAddress: "",
        buyerRegistrationType: "",
      }));
      return;
    }
    setInvoiceData((prev) => ({
      ...prev,
      buyerNTNCNIC: buyer?.cnic ?? "",
      buyerBusinessName:
        buyer?.buyerBusinessName ??
        buyer?.business_name ??
        buyer?.buyer_name ??
        "",
      buyerProvince: buyer?.province ?? "",
      buyerAddress: buyer?.address ?? "",
      buyerRegistrationType: buyer?.registration_type ?? "",
    }));
  };

  const handleProductSelect = (_, option) => {
    setSelectedProductOption(option);
    const productId = option?.value || option?.id || option || "";
    const product = products.find(
      (item) => String(getProductId(item)) === String(productId),
    );
    if (!product) return;
    setItemForm((prev) => {
      const baseTaxPercent =
        product?.sale_tax ?? prev.baseTaxPercent ?? prev.salesTaxApplicable;
      const taxPercent = baseTaxPercent;
      return {
        ...prev,
        productId,
        product_id: product?.product_id ?? productId,
        baseTaxPercent,
        productName:
          product?.name ?? product?.productName ?? product?.description ?? "",
        hsCode: product?.hscode ?? "",
        productDescription: product?.description ?? "",
        valueSalesExcludingST: product?.price ?? "",
        rate: taxPercent ? `${taxPercent}%` : prev.rate,
        uoM: product?.uom ?? prev.uom,
        salesTaxApplicable: taxPercent ?? prev.salesTaxApplicable,
        extraTax: product?.extra_tax ?? prev.extraTax,
        furtherTax: product?.further_tax ?? prev.furtherTax,
        sroScheduleNo: product?.sroScheduleNo ?? prev.sroScheduleNo,
        fedPayable: product?.fedPayable ?? prev.fedPayable,
        discount: product?.discount ?? prev.discount,
        saleType: product?.sale_type ?? prev.saleType,
        sroItemSerialNo: product?.sroItemSerialNo ?? prev.sroItemSerialNo,
      };
    });
  };

  const addOrUpdateItem = () => {
    if (!itemForm.productName && !itemForm.productDescription) {
      toast.error("Product select ya name/description add karein.");
      return;
    }
    const parsedItem = { ...itemForm };
    const selectedProductId =
      itemForm.productId ||
      itemForm.product_id ||
      selectedProductOption?.value ||
      selectedProductOption?.id ||
      selectedProductOption ||
      "";
    if (selectedProductId) {
      parsedItem.productId = selectedProductId;
      parsedItem.product_id = parsedItem.product_id || selectedProductId;
    }
    if (!parsedItem.productDescription) {
      parsedItem.productDescription =
        itemForm.productName || itemForm.hsCode || "Item";
    }
    if (!parsedItem.productName) {
      parsedItem.productName =
        itemForm.productDescription || itemForm.hsCode || "Item";
    }
    if (!parsedItem.hsCode) {
      parsedItem.hsCode = "N/A";
    }
    // Convert numeric fields (except salesTaxApplicable)
    numericItemFields.forEach((field) => {
      parsedItem[field] =
        itemForm[field] === "" || itemForm[field] === null
          ? 0
          : parseFloat(itemForm[field]);
    });
    // Track the base percentage for recalculation
    const baseTaxPercent = parseFloat(itemForm.baseTaxPercent);
    parsedItem.baseTaxPercent = Number.isFinite(baseTaxPercent)
      ? baseTaxPercent
      : parseFloat(itemForm.salesTaxApplicable) || 0;
    // Compute sales tax as amount (excluding further/extra taxes)
    const qty = parseFloat(parsedItem.quantity) || 0;
    const price = parseFloat(parsedItem.valueSalesExcludingST) || 0;
    const discount = parseFloat(parsedItem.discount) || 0;
    const base = Math.max(qty * price - discount, 0);
    const salesTaxPercent = parseFloat(itemForm.salesTaxApplicable) || 0;
    const salesTaxAmount =
      Math.round(((salesTaxPercent / 100) * base + Number.EPSILON) * 100) / 100;
    parsedItem.salesTaxApplicable = salesTaxAmount;
    // Compute total tax (sales tax amount + other percentage-based taxes)
    const otherTaxPercent =
      (parseFloat(parsedItem.extraTax) || 0) +
      (parseFloat(parsedItem.furtherTax) || 0) +
      (parseFloat(parsedItem.salesTaxWithheldAtSource) || 0);
    const otherTaxAmount = (otherTaxPercent / 100) * base;
    parsedItem.totalValues = base + salesTaxAmount + otherTaxAmount;
    setInvoiceData((prev) => {
      const items = [...prev.items];
      if (editingItemIndex !== null) {
        items[editingItemIndex] = parsedItem;
      } else {
        items.push(parsedItem);
      }
      return { ...prev, items };
    });
    setItemForm(emptyItem);
    setEditingItemIndex(null);
  };

  const editItem = (index) => {
    const selected = invoiceData.items[index];
    if (!selected) return;
    const qty = parseFloat(selected.quantity) || 0;
    const price = parseFloat(selected.valueSalesExcludingST) || 0;
    const discount = parseFloat(selected.discount) || 0;
    const base = Math.max(qty * price - discount, 0);
    const salesTaxAmount = parseFloat(selected.salesTaxApplicable) || 0;
    const salesTaxPercent =
      base > 0 ? (salesTaxAmount / base) * 100 : salesTaxAmount;
    const baseTaxPercent = Number.isFinite(selected.baseTaxPercent)
      ? selected.baseTaxPercent
      : salesTaxPercent;
    setItemForm({
      ...emptyItem,
      ...selected,
      quantity: String(selected.quantity ?? "1"),
      totalValues: String(selected.totalValues ?? "0"),
      valueSalesExcludingST: String(selected.valueSalesExcludingST ?? ""),
      fixedNotifiedValueOrRetailPrice: String(
        selected.fixedNotifiedValueOrRetailPrice ?? "0",
      ),
      baseTaxPercent: String(baseTaxPercent ?? ""),
      salesTaxApplicable: String(salesTaxPercent ?? ""),
      salesTaxWithheldAtSource: String(
        selected.salesTaxWithheldAtSource ?? "0",
      ),
      extraTax: String(selected.extraTax ?? "0"),
      furtherTax: String(selected.furtherTax ?? "0"),
      fedPayable: String(selected.fedPayable ?? "0"),
      discount: String(selected.discount ?? "0"),
    });
    setEditingItemIndex(index);
  };

  const deleteItem = (index) => {
    setInvoiceData((prev) => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index),
    }));
    setEditingItemIndex(null);
    setItemForm(emptyItem);
  };

  const resetForm = () => {
    setInvoiceData((prev) => ({
      ...emptyInvoice,
      sellerBranchName: sellerProfile.branchName,
      sellerBusinessName: sellerProfile.businessName,
      sellerNTNCNIC: sellerProfile.cnic,
      sellerProvince: sellerProfile.province,
      sellerAddress: sellerProfile.address,
      salesPersonName: salesPerson,
      invoiceDate: new Date().toISOString().slice(0, 10),
      dueDate: new Date().toISOString().slice(0, 10),
    }));
    setItemForm(emptyItem);
    setEditingSaleId(null);
    setEditingItemIndex(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!invoiceData.items.length) {
      toast.error("Add one item.");
      return;
    }
    setSubmitting(true);
    const editingId = editingSaleId || saleIdParam || null;
    let createdSaleId = editingId;
    try {
      const payload = {
        ...invoiceData,
        invoice: false,
        fbr: false,
        invoiceType: "Quotation",
        items: invoiceData.items,
        salePerson: invoiceData.salesPersonName || salesPerson,
        dueDate: invoiceData.dueDate,
      };
      if (editingId) {
        await putData(`api/sales/${editingId}`, payload);
        toast.success("Quotation updated.");
      } else {
        const response = await postData("api/sales", payload);
        const createdSale = response?.sale || response;
        createdSaleId = getSaleId(createdSale);
        if (createdSaleId && !payload.invoiceRefNo) {
          payload.invoiceRefNo = createdSaleId;
        }
        toast.success(
          createdSaleId
            ? `Quotation saved. #${createdSaleId}`
            : "Quotation saved.",
        );
        if (createdSaleId) {
          setTimeout(() => {
            navigate(`/quotation/${createdSaleId}?mode=simple`, {
              state: { sale: createdSale, invoiceNo: createdSaleId },
            });
          }, 500);
        }
        resetForm();
      }
    } catch (error) {
      toast.error(
        editingId ? "Quotation not updated." : "Quotation not created.",
      );
      console.error("Submit quotation failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const loadSaleForEdit = async (saleId) => {
    if (!saleId) {
      toast.error("Sale ID not found.");
      return;
    }

    try {
      showLoader();

      const response = await getData(`api/sales/${saleId}`);

      const payload =
        response?.sale || response?.data?.sale || response?.data || response;

      const payloadItems = parseItemsField(payload?.items);
      const mappedItems = payloadItems.map((item) => ({
        productId: item?.productId ?? item?.product_id ?? "",
        product_id: item?.product_id ?? item?.productId ?? "",
        productName: item.productName || "",
        productDescription: item.productDescription || "",
        quantity: Number(item.quantity || 1),
        hsCode: item.hsCode || "",
        saleType: item.saleType || "",
        rate: item.rate || "",
        baseTaxPercent: Number.parseFloat(
          String(item.rate || "").replace("%", ""),
        ),
        valueSalesExcludingST: Number(item.valueSalesExcludingST || 0),
        salesTaxApplicable: Number(item.salesTaxApplicable || 0),
        discount: Number(item.discount || 0),
        extraTax: Number(item.extraTax || 0),
        furtherTax: Number(item.furtherTax || 0),
        salesTaxWithheldAtSource: Number(item.salesTaxWithheldAtSource || 0),
      }));

      // ---- 🛠 Set Invoice Data with full mapping ----
      setInvoiceData({
        ...emptyInvoice,
        invoiceType: payload?.invoiceType ?? "Quotation",

        invoiceDate: payload?.invoiceDate
          ? payload.invoiceDate.slice(0, 10)
          : emptyInvoice.invoiceDate,

        dueDate: payload?.dueDate
          ? payload.dueDate.slice(0, 10)
          : emptyInvoice.dueDate,

        sellerBusinessName:
          payload?.sellerBusinessName || sellerProfile.businessName,
        sellerNTNCNIC: payload?.sellerNTNCNIC || sellerProfile.cnic,
        sellerProvince: payload?.sellerProvince || sellerProfile.province,
        sellerAddress: payload?.sellerAddress || sellerProfile.address,

        buyerBusinessName: payload?.buyerBusinessName || "",
        buyerNTNCNIC: payload?.buyerNTNCNIC || "",
        buyerProvince: payload?.buyerProvince || "",
        buyerAddress: payload?.buyerAddress || "",
        buyerRegistrationType: payload?.buyerRegistrationType || "",

        invoiceRefNo: payload?.invoiceRefNo || payload?.id || saleId,

        items: mappedItems,
      });

      setEditingSaleId(saleId);
      setItemForm(emptyItem);
      setEditingItemIndex(null);

      toast.success("Sale loaded and mapped successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load sale.");
    } finally {
      hideLoader();
    }
  };

  const previewTotals = useMemo(() => {
    const items = invoiceData.items || [];
    return items.reduce(
      (acc, item) => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.valueSalesExcludingST) || 0;
        const discount = parseFloat(item.discount) || 0;
        const gross = qty * price;
        const lineBase = Math.max(gross - discount, 0);
        const tax = calcLineTax(item);
        acc.subtotal += gross;
        acc.tax += tax;
        acc.discount += discount;
        acc.total += lineBase + tax;
        return acc;
      },
      { subtotal: 0, tax: 0, discount: 0, total: 0 },
    );
  }, [invoiceData.items]);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />
        <div className="dashboard-page">
          <main className="">
            <section className="sample-page">
              <div className="sample-page__header">
                <p className="eyebrow">
                  {editingSaleId || saleIdParam
                    ? "Update quotation"
                    : "Create a quotation"}
                </p>
                <h1>
                  {editingSaleId || saleIdParam
                    ? "Edit Quotation"
                    : "Quotations"}
                </h1>
              </div>

              <form
                className="quote-form"
                onSubmit={(event) => event.preventDefault()}
              >
                <div className="quote-preview-card">
                  <div className="quote-preview-header">
                    <div>
                      <p className="eyebrow">Quotation</p>
                      <h2>
                        {invoiceData.invoiceRefNo ||
                          editingSaleId ||
                          saleIdParam ||
                          "Draft"}
                      </h2>
                      <div className="quote-customer">
                        <div className="buyer-search-select">
                          <Autocomplete
                            fullWidth
                            options={buyerOptions}
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(opt, val) =>
                              String(opt.value) === String(val.value)
                            }
                            value={selectedBuyerOption}
                            onChange={handleBuyerSelect}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search Buyer"
                                label="Select Buyer"
                                size="small"
                              />
                            )}
                          />
                          <TextField
                            type="text"
                            className="quote-inline-input"
                            placeholder="Enter Customer BusinessName"
                            label="Customer BusinessName"
                            size="small"
                            fullWidth
                            value={invoiceData.buyerBusinessName}
                            onChange={(e) =>
                              setInvoiceData((prev) => ({
                                ...prev,
                                buyerBusinessName: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="quote-customer-info">
                          <p>{invoiceData.buyerBusinessName || "-"}</p>
                          <small>{invoiceData.buyerNTNCNIC || "-"}</small>
                          <small>{invoiceData.buyerAddress || "-"}</small>
                        </div>
                      </div>
                    </div>
                    <div className="quote-preview-meta">
                      <label className="form-field">
                        <span>Invoice date</span>
                        <TextField
                          type="date"
                          name="invoiceDate"
                          value={invoiceData.invoiceDate}
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              invoiceDate: e.target.value,
                            }))
                          }
                          required
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </label>
                      <div className="quote-meta-row">
                        <label className="form-field">
                          <span>Due date</span>
                          <TextField
                            type="date"
                            name="dueDate"
                            value={invoiceData.dueDate}
                            onChange={(e) =>
                              setInvoiceData((prev) => ({
                                ...prev,
                                dueDate: e.target.value,
                              }))
                            }
                            required
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </label>
                      </div>
                      <div className="quote-meta-row">
                        <span className="muted-text">Seller</span>
                        <strong>{invoiceData.sellerBusinessName || "-"}</strong>
                      </div>
                      <div className="quote-meta-row">
                        <span className="muted-text">Sales person</span>
                        <strong>
                          {invoiceData.salesPersonName || salesPerson || "-"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="quote-table-wrapper">
                    <table className="quote-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Product</th>
                          <th>Description</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Tax %</th>
                          <th>Amount</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceData.items.length === 0 && (
                          <tr>
                            <td colSpan="8" className="empty-state">
                              No items added yet.
                            </td>
                          </tr>
                        )}
                        {invoiceData.items.map((item, idx) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const price =
                            parseFloat(item.valueSalesExcludingST) || 0;
                          const discount = parseFloat(item.discount) || 0;
                          const gross = qty * price;
                          const lineBase = Math.max(gross - discount, 0);
                          const tax = calcLineTax(item);
                          const salesTaxAmount =
                            parseFloat(item.salesTaxApplicable) || 0;
                          const baseForRate = lineBase > 0 ? lineBase : 1;
                          const salesTaxPercent =
                            (salesTaxAmount / baseForRate) * 100;
                          const total = lineBase + tax;
                          const taxPercent =
                            salesTaxPercent +
                            (parseFloat(item.extraTax) || 0) +
                            (parseFloat(item.furtherTax) || 0) +
                            (parseFloat(item.salesTaxWithheldAtSource) || 0);
                          return (
                            <tr key={`${item.hsCode}-${idx}`}>
                              <td>{idx + 1}</td>
                              <td>
                                {item.productName || item.hsCode || "Item"}
                              </td>
                              <td>{item.productDescription || "-"}</td>
                              <td>{qty}</td>
                              <td>{price}</td>
                              <td>{taxPercent.toFixed(2)}%</td>
                              <td>{total.toFixed(2)}</td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    onClick={() => editItem(idx)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => deleteItem(idx)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="quote-add-line">
                    <div className="quote-add-fields three-col">
                      <Autocomplete
                        fullWidth
                        options={productOptions}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(opt, val) =>
                          String(opt.value) === String(val.value)
                        }
                        value={selectedProductOption}
                        onChange={handleProductSelect}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search product"
                            label="Choose product"
                            size="small"
                          />
                        )}
                      />

                      <TextField
                        name="quantity"
                        value={itemForm.quantity}
                        onChange={handleItemChange}
                        inputMode="decimal"
                        label="Quantity"
                        size="small"
                        fullWidth
                      />

                      <TextField
                        name="valueSalesExcludingST"
                        value={itemForm.valueSalesExcludingST}
                        // onChange={handleItemChange}
                        inputMode="decimal"
                        label="Unit price"
                        size="small"
                        fullWidth
                        disabled
                      />

                      <TextField
                        name="salesTaxApplicable"
                        value={itemForm.salesTaxApplicable}
                        // onChange={handleItemChange}
                        inputMode="decimal"
                        label="Tax %"
                        size="small"
                        fullWidth
                        disabled
                      />

                      <TextField
                        name="discount"
                        value={itemForm.discount}
                        // onChange={handleItemChange}
                        inputMode="decimal"
                        label="Discount"
                        size="small"
                        fullWidth
                        disabled
                      />
                    </div>
                    <div className="quote-add-actions">
                      <button
                        type="button"
                        className="primary-btn quote-add-btn"
                        onClick={addOrUpdateItem}
                      >
                        {editingItemIndex !== null
                          ? "Update Product"
                          : "Add Product"}
                      </button>
                    </div>
                  </div>

                  <div className="quote-footer-actions">
                    <div className="quote-totals-stack">
                      <div>
                        <span>Subtotal</span>
                        <strong className="ml-15">
                          {previewTotals.subtotal.toFixed(2)}
                        </strong>
                      </div>
                      <div>
                        <span>Tax</span>
                        <strong className="ml-15">
                          {previewTotals.tax.toFixed(2)}
                        </strong>
                      </div>
                      <div>
                        <span>Discount</span>
                        <strong className="ml-15">
                          -{previewTotals.discount.toFixed(2)}
                        </strong>
                      </div>
                      <div className="quote-grand">
                        <span>Total</span>
                        <strong className="ml-15">
                          {previewTotals.total.toFixed(2)}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="primary-btn quote-submit-btn"
                    disabled={submitting}
                    onClick={handleSubmit}
                  >
                    {submitting
                      ? "Please wait..."
                      : editingSaleId || saleIdParam
                        ? "Update quotation"
                        : "Add Quotation"}
                  </button>
                </div>
              </form>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
